import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Home.css';

const Home = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const features = [
    {
      title: 'Premium Milk',
      description: 'Hand-milked, antibiotic-free A2 dairy with scientific verification.',
      link: '/premium-milk',
      icon: '🥛'
    },
    {
      title: 'PharmApp',
      description: 'Intelligent farm management system with real-time monitoring.',
      link: '/pharmapp',
      icon: '📱'
    },
    {
      title: 'Investor Vision',
      description: 'ESG-focused growth with transparent metrics and sustainable practices.',
      link: '/investors',
      icon: '📈'
    }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background parallax-container">
          <div className="parallax-bg" style={{
            backgroundImage: 'linear-gradient(to bottom, rgba(11, 19, 43, 0.7), rgba(11, 19, 43, 0.4), transparent), url(https://images.unsplash.com/photo-1560493676-04071c5f467b?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-headline"
          >
            Ethical, Antibiotic-Free, Farm-Traceable Milk for Urban Families
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hero-subheadline"
          >
            Hand-milked, lab-tested A2 & premium milk from named, stress-free animals — delivered chilled from our fields to your doorstep.
          </motion.p>
          <motion.ul
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="hero-proof-bullets"
          >
            <li>✓ Zero detectable antibiotic residue (third-party lab tested for every batch)</li>
            <li>✓ Self-grown, chemical-free fodder for higher natural CLA and Omega-3s</li>
            <li>✓ QR code traceability from animal group to bottle, every single day</li>
          </motion.ul>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="hero-ctas"
          >
            <Link to="/premium-milk#join" className="btn btn-primary">
              Start Your Subscription
            </Link>
            <Link to="/premium-milk" className="btn btn-outline" onClick={(e) => {
              e.preventDefault();
              document.querySelector('.premium-milk-page .hero-section')?.scrollIntoView({ behavior: 'smooth' });
              setTimeout(() => {
                const modalBtn = document.querySelector('.premium-milk-page .hero-ctas button');
                if (modalBtn) modalBtn.click();
              }, 500);
            }}>
              See Today's Lab Report
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why PharmWealth Exists Section */}
      <section className="about-section section">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="why-exists-content"
          >
            <h2 className="section-title">Why PharmWealth Milk Exists</h2>
            <div className="gold-line"></div>
            <div className="why-exists-split">
              <div className="why-exists-text">
                <p className="about-text">
                  Most Indians still drink milk they cannot truly trust. A recent multi-state study showed antibiotic residues present in a worrying share of milk samples, with many crossing safety limits. Fragmented supply chains, preventive antibiotic use, and poor testing create silent risk in something families consume every day.
                </p>
                <p className="about-text">
                  At the same time, demand for cleaner, more natural A2 and organic milk is exploding. PharmWealth exists to answer one question: <em>What if milk could be as safe, transparent, and humane as the food you choose for your own child?</em>
                </p>
              </div>
              <div className="why-exists-visual">
                <div className="bottle-pour-visual">
                  🥛
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section section">
        <div className="container">
          <motion.h2
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            Our Offerings
          </motion.h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.2 }}
                className="feature-card card card-gold"
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <Link to={feature.link} className="btn btn-secondary">
                  Learn More →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section">
        <div className="container">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="cta-block"
          >
            <h2>Experience Premium Dairy, Reimagined</h2>
            <p>Join thousands of families who trust PharmWealth for purity, traceability, and ethical excellence.</p>
            <Link to="/premium-milk" className="btn btn-primary">
              Explore Premium Milk
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;

